import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099SInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-S instructions for Tax Year 2025"
          description="Learn how to correctly fill out IRS Form 1099-S for Tax Year 2025. TaxZerone provides step-by-step instructions for reporting real estate sales accurately."
          keywords=""
          canonical="resources/information-returns/form-1099-s-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-S</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-s-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-S at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        Form 1099-S reports proceed from real estate sales or
                        exchanges, so the IRS and transferor can properly
                        account for the transaction.
                      </span>
                    </li>

                    <li>
                      <span>
                        Transferor copies must be furnished by
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>
                        , if filing on paper or
                        <strong style={{ fontWeight: 600 }}> March 31</strong>,
                        if filing electronically.
                      </span>
                    </li>

                    <li>
                      <span>
                        This article covers line-by-line instructions including
                        filer and transferor details,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong> (Date
                        of Closing),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2 </strong>{' '}
                        (Gross Proceeds),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>{' '}
                        (Address or Legal Description),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>{' '}
                        (Transferor Received or Will Receive Property or
                        Services),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 5</strong>{' '}
                        (Foreign Person Indicator), and{' '}
                        <strong style={{ fontWeight: 600 }}> Boxes 6</strong>{' '}
                        (Buyer's Part of Real Estate Tax, Property Address, and
                        Account Number).
                      </span>
                    </li>

                    <li>
                      <span>
                        Late or incorrect filing may result in IRS penalties
                        ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-S?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1099-S, also known as &quot;Proceeds from Real Estate
                  Transactions,&quot; is a tax document used to report the gross
                  proceeds from the sale or exchange of real estate. If you are
                  a real estate agent, broker, or closing agent and you
                  facilitate a real estate transaction worth $600 or more, you
                  are required to file a Form 1099-S.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  This form is submitted to the IRS and a copy is provided to
                  the seller to report the sale proceeds.
                </Typography>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-S?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  The responsibility to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/ "
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-S
                  </Link>{' '}
                  lies with the person or entity that handles the closing of the
                  real estate transaction. This can include:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Real estate brokers and agents</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Title companies</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Attorneys</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Escrow companies</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Mortgage lenders</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Generally, the closing agent or entity will take care of the
                  filing. If you are involved in a real estate transaction, make
                  sure that Form 1099-S is filed correctly to comply with IRS
                  regulations and avoid penalties.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-S due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filers must be aware of two important deadlines related to
                  Form 1099-S:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for filers to submit Form 1099-S to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-S with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-S deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099sinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-S with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Transferor copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filers must share copies of Form 1099-S to transferors by
                  January 31st of the year following the relevant tax year. This
                  allows transferors to accurately file their tax returns on
                  time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> * if the due date falls on the weekend or
                  federal holiday, it is extended to the next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-S can lead to IRS penalties. To avoid potential
                  penalties, filers should take steps to ensure that Form 1099-S
                  is filed and distributed both accurately and on time.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-S penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 1099-S
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-S penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-S forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099sinstructions`}
                    >
                      E-file Form 1099-S
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-S Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-S.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-1099-s-filter-details.webp" />
                  <source srcSet="/assets/images/form-1099-s-filter-details.png" />
                  <img
                    src="/assets/images/form-1099-s-filter-details.png"
                    loading="lazy"
                    alt="Form 1099-s-filer details"
                    width={525}
                    height={208}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name, complete address, and TIN (SSN if you&apos;re
                  an individual; EIN if you&apos;re a business).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Transferor details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-transferor-details.webp" />
                  <source srcSet="/assets/images/form-1099-s-transferor-details.png" />
                  <img
                    src="/assets/images/form-1099-s-transferor-details.png"
                    loading="lazy"
                    alt="Form 1099-s-transferor details"
                    width={525}
                    height={284}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  Enter the transferor’s name, complete address, and TIN (SSN
                  for an individual; EIN for a business).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Date of Closing
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-date-of-closing.webp" />
                  <source srcSet="/assets/images/form-1099-s-date-of-closing.png" />
                  <img
                    src="/assets/images/form-1099-s-date-of-closing.png"
                    loading="lazy"
                    alt="Form 1099-s-box-1-date of closing"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the date when the real estate transaction was finalized.
                  This date depends on the document used:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>If using a Closing Disclosure:</b> The closing date is
                    the same as the Closing Disclosure date.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>If not using a Closing Disclosure:</b> The closing date
                    is whichever comes first:
                  </li>
                  <ul>
                    {' '}
                    <li style={{ marginBottom: 12 }}>
                      The date when the property title transfers to the new
                      owner, or
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The date when the financial responsibilities and benefits
                      of ownership shift to the buyer (also called the
                      transferee).
                    </li>
                  </ul>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Gross Proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-gross-proceed.webp" />
                  <source srcSet="/assets/images/form-1099-s-gross-proceed.png" />
                  <img
                    src="/assets/images/form-1099-s-gross-proceed.png"
                    loading="lazy"
                    alt="Form 1099-s-box-2-gross proceeds"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount received from the sale or exchange of
                  real estate. This includes:
                </Typography>

                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Cash received or to be received by the seller (transferor)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The principal amount of any note payable to the seller
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any note or mortgage paid off at settlement
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any liability assumed by the buyer (transferee) or attached
                    to the property
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For contingent payment transactions, include the maximum
                  determinable proceeds.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, fontStyle: 'italic' }}
                >
                  Special cases:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Like-kind exchange with no reportable proceeds: Enter -0-
                    (zero) in box 2 and mark the checkbox in box 4
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Do not include:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Value of property or services received by the seller
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Separately stated cash received for personal property (e.g.,
                    draperies, appliances)
                  </li>
                </ul>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Do not reduce the expenses paid by the seller
                  (e.g., commissions, deed preparation, advertising, legal fees)
                  from the gross proceeds.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Address (Include city, state, and ZIP code) or Legal
                  description
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-property-identification.webp" />
                  <source srcSet="/assets/images/form-1099-s-property-identification.png" />
                  <img
                    src="/assets/images/form-1099-s-property-identification.png"
                    loading="lazy"
                    alt="Form 1099-s-box-3-address (including city,state,and zip code)or legal description"
                    width={432}
                    height={105}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter one of the following to identify the property:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>Complete address:</li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>Street number and name</li>
                    <li style={{ marginBottom: 12 }}>City</li>{' '}
                    <li style={{ marginBottom: 12 }}>State</li>
                    <li style={{ marginBottom: 12 }}>ZIP code</li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    Legal description (if the address is not sufficient):
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      Include details such as section, lot, and block numbers
                    </li>
                  </ul>
                </ol>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, fontStyle: 'italic' }}
                >
                  Special cases:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Timber royalties: Enter &quot;Timber royalties&quot;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Lump-sum timber payments: Enter &quot;Lump-sum timber
                    payment&quot;
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the address alone doesn&apos;t clearly
                  identify the property, include both the address and the legal
                  description.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Check here if the transferor received or will receive
                  property or services as part of the consideration
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-non-cash.webp" />
                  <source srcSet="/assets/images/form-1099-s-non-cash.png" />
                  <img
                    src="/assets/images/form-1099-s-non-cash.png"
                    loading="lazy"
                    alt="Form 1099-s-box-4-check here if the transferor received or will receive property or services as part of the consideration"
                    width={432}
                    height={53}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The seller (transferor) received or will receive any of the
                  following as part of the payment for the property:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Property (other than cash)
                  </li>
                  <li style={{ marginBottom: 12 }}>Services</li>{' '}
                </ol>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Do not check this box for:
                </Alert>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Cash payments</li>
                  <li style={{ marginBottom: 12 }}>
                    Items treated as cash when calculating gross proceeds
                  </li>{' '}
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To mark this box: Enter an &quot;X&quot; in the checkbox in
                  Box 4.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 5: Foreign Transferor Indicator
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-foreign-transferor.webp" />
                  <source srcSet="/assets/images/form-1099-s-foreign-transferor.png" />
                  <img
                    src="/assets/images/form-1099-s-foreign-transferor.png"
                    loading="lazy"
                    alt="Form 1099-s-box-5-foreign transferor indicator"
                    width={432}
                    height={79}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check this box if the seller (transferor) is a foreign person,
                  which includes:
                </Typography>

                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>Nonresident alien</li>
                  <li style={{ marginBottom: 12 }}>Foreign partnership</li>
                  <li style={{ marginBottom: 12 }}>Foreign estate</li>
                  <li style={{ marginBottom: 12 }}>Foreign trust</li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To mark this box: Enter an &quot;X&quot; in the checkbox in
                  Box 5.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Important note:</b> If this box is checked, there may be
                  tax withholding requirements for properties sold by a foreign
                  transferor.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Buyer&apos;s part of real estate tax
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-s-buyer-share.webp" />
                  <source srcSet="/assets/images/form-1099-s-buyer-share.png" />
                  <img
                    src="/assets/images/form-1099-s-buyer-share.png"
                    loading="lazy"
                    alt="Form 1099-s-box-6-buyer's part of real estate tax"
                    width={432}
                    height={54}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  For residential real estate transactions:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the amount of prepaid real estate tax that is
                    allocated to the buyer.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Do not report real estate taxes paid in arrears (after the
                    due date).
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Use the information from the Closing Disclosure or a similar
                    form provided at closing.
                  </li>{' '}
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Example calculation:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Property sold: Residential
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Local tax system: Annual real estate tax paid in advance
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Total tax paid by seller for the year: $1,200
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Sale timing: End of the 9th month of the tax year.
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Buyer&apos;s share: 3 months (25% of the year)
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Amount to report in Box 6: $300 ($1,200 × 25%)
                  </li>{' '}
                </ul>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> This example assumes the Closing Disclosure
                  shows the seller paid $1,200 in advance for the full
                  year&apos;s taxes.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-S
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-S
                  </Link>
                  , either online filing or by postal mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-S
                  </Link>{' '}
                  which is efficient, accurate and quick processing
                  notifications.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here&apos;s how to e-file Form 1099-S:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Filer’s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Transferor’s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Gross proceeds</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review the entered information:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Ensure all details are correct and complete.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form to the IRS:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Send the form securely through the e-file service.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Share a copy with the transferor:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Provide a copy of the form to the transferor.
                    </li>
                  </ol>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing is still an option. Follow
                  these steps to file Form 1099-S on paper:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>Download Form 1099-S:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Visit the official IRS website (
                      <Link
                        href="https://www.irs.gov/"
                        target="_blank"
                        sx={{ color: '#2020ff' }}
                      >
                        www.irs.gov
                      </Link>
                      ) to download the form.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>Complete the form:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Use black ink to fill out all required fields, including:
                    </li>
                    <li style={{ marginBottom: 12 }}>Filer’s information</li>
                    <li style={{ marginBottom: 12 }}>
                      Transferor’s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Gross proceeds</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Make a copy:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Keep a copy for your records.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Mail the original form:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Send the original form to the IRS address specified in the
                      Form 1099 General Instructions. The address varies based
                      on your state. Refer to the &quot;Where to Send <br />
                      <Link href="#mailadd" sx={{ color: '#0202ff' }}>
                        {' '}
                        Form 1099-S - Mailing Address
                      </Link>
                      &quot; section to find the correct address for your state.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the transferor:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Provide a copy to the transferor by January 31st of the
                      year following the reportable transaction.
                    </li>
                  </ol>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 6 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms early to ensure timely arrival before the
                      deadline.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" id="mailadd" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                    id="1099-s-mailing-address"
                  >
                    file Form 1099-S electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-S - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-S varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-S:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-S to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-S with TaxZerone?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-S
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here’s what you need
                  to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-S:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Filer details</li>
                  <li style={{ marginBottom: 16 }}>Transfer or details</li>
                  <li style={{ marginBottom: 16 }}>
                    Property information including closing date, gross proceeds,
                    and address.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-S using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, mt: 1.5 }}>
                  <b>Step 1: Complete Form 1099-S</b>
                  <br />
                  Open Form 1099-S and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-S to
                  the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the transferor copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, share a
                  copy of the completed Form 1099-S to the transferor.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-S with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  E-filing Form 1099-S with TaxZerone offers several advantages,
                  including:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS form validations:</b> Automated checks for errors
                      or missing information to reduce the chances of rejection.
                      submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Email transferor copies:</b> Share transferor copies
                      of Form 1099-S via email and eliminate the need for manual
                      mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Secure portal access (ZeroneVault):</b> Debtors can
                      access their return copies anytime, anywhere through the
                      secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Competitive pricing:</b> Industry-leading rates,
                      starting as low as $0.59 per form.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By e-filing with TaxZerone, you can simplify your Form 1099-S
                  filing, save time, and reduce errors.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax filing by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-S.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099sinstructions`}
              >
                E-file Form 1099-S
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099SInstructions;
